/* Parenting Hub — predictive search
   Wires any <form data-ph-search> with a .ph-search-js input + .ph-suggest panel
   to Shopify's native /search/suggest.json API. Debounced, keyboard-navigable,
   closes on outside click / Escape. No theme dependencies. */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function buildUrl(term, types, limit) {
    var params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', types);
    params.set('resources[limit]', String(limit));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', '');
    return ROOT.replace(/\/$/, '') + '/search/suggest.json?' + params.toString();
  }

  // Standard Search API fallback. Predictive search (/search/suggest.json) is a
  // typeahead index that tokenizes + ANDs terms, so a full-sentence query (e.g.
  // pasting an exact article title) frequently returns nothing. The regular
  // /search endpoint matches phrases reliably. We hit it via the Section
  // Rendering API (&section_id=) so we get back a small HTML fragment instead of
  // the whole page, then parse the result links out of it.
  function buildFallbackUrl(term, types) {
    var params = new URLSearchParams();
    params.set('q', term);
    params.set('type', types);
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'last');
    params.set('section_id', 'search-others');
    return ROOT.replace(/\/$/, '') + '/search?' + params.toString();
  }

  function hasResults(data) {
    var r = (data && data.resources && data.resources.results) || {};
    return (r.articles && r.articles.length) ||
      (r.pages && r.pages.length) ||
      (r.products && r.products.length) ||
      (r.queries && r.queries.length);
  }

  // Parse the search-others section HTML into the shape render() expects.
  // The section markup is: .t4s-result__item > .t4s-result-infos > a.t4s-result__item-title
  function parseFallback(htmlText) {
    var doc = new DOMParser().parseFromString(htmlText, 'text/html');
    var articles = [];
    doc.querySelectorAll('.t4s-result__item-title').forEach(function (a) {
      var href = a.getAttribute('href');
      var title = (a.textContent || '').trim();
      if (href && title) articles.push({ url: href, title: title });
    });
    return { resources: { results: { articles: articles, pages: [], products: [] } } };
  }

  // Keep only results that live under a given blog, e.g. /blogs/parenting-hub/.
  // Predictive/standard search can't scope to a blog server-side, so we filter
  // by URL path here. Pages/products are dropped entirely when a blog is set.
  function filterByBlog(data, blogHandle) {
    if (!blogHandle) return data;
    var r = (data && data.resources && data.resources.results) || {};
    var prefix = '/blogs/' + blogHandle + '/';
    function inBlog(item) {
      var url = (item && item.url) || '';
      // Strip origin + locale root so the match works on relative or absolute urls.
      var path = url.replace(/^https?:\/\/[^/]+/, '');
      return path.indexOf(prefix) !== -1;
    }
    return {
      resources: {
        results: {
          articles: (r.articles || []).filter(inBlog),
          pages: [],
          products: [],
          queries: []
        }
      }
    };
  }

  function render(panel, data, term, searchUrl) {
    var r = (data && data.resources && data.resources.results) || {};
    var articles = r.articles || [];
    var pages = r.pages || [];
    var products = r.products || [];
    var queries = r.queries || [];
    var html = '';

    if (queries.length) {
      html += '<div class="ph-suggest__group"><ul class="ph-suggest__list">';
      queries.forEach(function (q) {
        html += '<li><a class="ph-suggest__item ph-suggest__item--q" href="' + esc(q.url) + '">' +
          '<span class="ph-suggest__ico" aria-hidden="true">&#9906;</span>' +
          '<span class="ph-suggest__txt">' + esc(q.text) + '</span></a></li>';
      });
      html += '</ul></div>';
    }

    if (articles.length) {
      html += '<div class="ph-suggest__group"><div class="ph-suggest__head">Articles</div><ul class="ph-suggest__list">';
      articles.forEach(function (a) {
        html += '<li><a class="ph-suggest__item" href="' + esc(a.url) + '">' +
          '<span class="ph-suggest__txt">' + esc(a.title) + '</span></a></li>';
      });
      html += '</ul></div>';
    }

    if (pages.length) {
      html += '<div class="ph-suggest__group"><div class="ph-suggest__head">Pages</div><ul class="ph-suggest__list">';
      pages.forEach(function (p) {
        html += '<li><a class="ph-suggest__item" href="' + esc(p.url) + '">' +
          '<span class="ph-suggest__txt">' + esc(p.title) + '</span></a></li>';
      });
      html += '</ul></div>';
    }

    if (products.length) {
      html += '<div class="ph-suggest__group"><div class="ph-suggest__head">Products</div><ul class="ph-suggest__list">';
      products.forEach(function (p) {
        var img = (p.featured_image && p.featured_image.url) ? '<img class="ph-suggest__img" src="' + esc(p.featured_image.url) + '" alt="" width="32" height="32" loading="lazy">' : '';
        html += '<li><a class="ph-suggest__item ph-suggest__item--product" href="' + esc(p.url) + '">' +
          img + '<span class="ph-suggest__txt">' + esc(p.title) + '</span></a></li>';
      });
      html += '</ul></div>';
    }

    if (!html) {
      html = '<div class="ph-suggest__empty">No results for &ldquo;' + esc(term) + '&rdquo;</div>';
    } else {
      html += '<a class="ph-suggest__all" href="' + esc(searchUrl) + '">See all results for &ldquo;' + esc(term) + '&rdquo;</a>';
    }

    panel.innerHTML = html;
    panel.hidden = false;
  }

  function init(form) {
    var input = form.querySelector('.ph-search-js');
    var panel = form.querySelector('.ph-suggest');
    if (!input || !panel) return;

    var types = form.getAttribute('data-types') || 'article';
    var blog = (form.getAttribute('data-blog') || '').trim();
    var limit = parseInt(form.getAttribute('data-limit') || '6', 10);
    // When scoped to a blog, over-fetch so filtering still leaves enough results.
    var fetchLimit = blog ? Math.min(limit * 3, 10) : limit;
    var minChars = 2;
    var controller = null;
    var activeIndex = -1;

    function close() { panel.hidden = true; panel.innerHTML = ''; activeIndex = -1; }

    function searchUrl(term) {
      // Keep the user on the blog URL: /blogs/<handle>?q=term. The blog page
      // renders normally; ph-search's in-page renderer (see initResults) reads
      // ?q= and swaps the hub feed for the results list. No trip to /search.
      if (blog) {
        return ROOT.replace(/\/$/, '') + '/blogs/' + blog + '?q=' + encodeURIComponent(term);
      }
      var u = ROOT.replace(/\/$/, '') + '/search?q=' + encodeURIComponent(term);
      if (types && types.indexOf('product') === -1) u += '&type=' + encodeURIComponent(types.split(',')[0]);
      return u;
    }

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < minChars) { close(); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      panel.hidden = false;
      panel.innerHTML = '<div class="ph-suggest__loading">Searching&hellip;</div>';
      fetch(buildUrl(term, types, fetchLimit), { signal: controller.signal, headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var scoped = filterByBlog(data, blog);
          // Predictive search found nothing in-scope — retry the full-phrase
          // Search API before showing "No results". Handles pasting an exact
          // article title, and blog-scoped queries the typeahead index misses.
          if (!hasResults(scoped)) {
            return fetch(buildFallbackUrl(term, types), { signal: controller.signal })
              .then(function (res) { return res.text(); })
              .then(function (htmlText) { render(panel, filterByBlog(parseFallback(htmlText), blog), term, searchUrl(term)); });
          }
          render(panel, scoped, term, searchUrl(term));
        })
        .catch(function (err) { if (err.name !== 'AbortError') close(); });
    }, 200);

    input.addEventListener('input', run);
    input.addEventListener('focus', function () { if (input.value.trim().length >= minChars && panel.innerHTML) panel.hidden = false; });

    input.addEventListener('keydown', function (e) {
      var items = panel.querySelectorAll('.ph-suggest__item, .ph-suggest__all');
      if (e.key === 'Escape') { close(); return; }
      if (!items.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && items[activeIndex]) { e.preventDefault(); window.location.href = items[activeIndex].href; }
        return;
      } else { return; }
      items.forEach(function (el, i) { el.classList.toggle('is-active', i === activeIndex); });
      if (items[activeIndex]) items[activeIndex].scrollIntoView({ block: 'nearest' });
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
  }

  // Safety net for the FOUC cloak (.ph-cloak hidden via head CSS, revealed by
  // each section's inline style block). If a section ever renders without its
  // reveal rule, this guarantees content is never left permanently hidden.
  function uncloak() {
    document.querySelectorAll('.ph-cloak').forEach(function (el) {
      el.style.visibility = 'visible';
    });
  }

  // ---------- In-page results on the blog page ----------
  // Search keeps the user on /blogs/<handle>?q=term. Liquid on the blog template
  // can't read ?q=, so when that param is present we fetch the blog-scoped
  // article results here and render the full list in place of the hub feed.
  function getParam(name) {
    var m = new RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }

  function resultsHtml(articles, term) {
    var html = '<div class="ph-main__tagged ph-search-results">' +
      '<h2 class="ph-col__title ph-tagged__title"><span class="ph-tagged__eyebrow">Search</span>' +
      '<span>' + esc(term) + '</span></h2>';
    if (!articles.length) {
      html += '<p class="ph-tagged__empty">No results for &ldquo;' + esc(term) + '&rdquo;.</p>';
    } else {
      html += '<ul class="ph-tagged__list">';
      articles.forEach(function (a) {
        html += '<li class="ph-tagged__item">' +
          '<a class="ph-tagged__link" href="' + esc(a.url) + '">' + esc(a.title) + '</a></li>';
      });
      html += '</ul>';
    }
    html += '</div>';
    return html;
  }

  function initResults() {
    var term = getParam('q').trim();
    if (!term) return;
    // The hub feed container to replace. Prefer the two-column landing row;
    // fall back to any .ph-main__row. Only runs where a hub feed exists.
    var host = document.querySelector('.ph-main__row, .ph-main__tagged');
    if (!host) return;
    // Blog handle: read from the search form on the page, else the URL path.
    var form = document.querySelector('form[data-ph-search]');
    var blog = (form && (form.getAttribute('data-blog') || '').trim()) || '';
    if (!blog) {
      var mp = window.location.pathname.match(/\/blogs\/([^/]+)/);
      blog = mp ? mp[1] : '';
    }
    var types = (form && form.getAttribute('data-types')) || 'article';

    // Reflect the term in any search inputs on the page.
    document.querySelectorAll('.ph-search-js').forEach(function (i) { if (!i.value) i.value = term; });

    host.innerHTML = '<div class="ph-search-results"><p class="ph-tagged__empty">Searching&hellip;</p></div>';

    fetch(buildUrl(term, types, 30), { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var scoped = filterByBlog(data, blog);
        if (!hasResults(scoped)) {
          return fetch(buildFallbackUrl(term, types))
            .then(function (res) { return res.text(); })
            .then(function (t) { return filterByBlog(parseFallback(t), blog); });
        }
        return scoped;
      })
      .then(function (finalData) {
        var arts = (finalData && finalData.resources && finalData.resources.results.articles) || [];
        host.innerHTML = resultsHtml(arts, term);
      })
      .catch(function () { host.innerHTML = resultsHtml([], term); });
  }

  function boot() {
    document.querySelectorAll('form[data-ph-search]').forEach(init);
    initResults();
    uncloak();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Re-init when sections reload in the theme editor
  document.addEventListener('shopify:section:load', boot);

  /* ---------- Mobile drawer toggle ---------- */
  function openDrawer() {
    document.body.classList.add('ph-drawer-open');
    var btn = document.querySelector('[data-ph-drawer-open]');
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }
  function closeDrawer() {
    document.body.classList.remove('ph-drawer-open');
    var btn = document.querySelector('[data-ph-drawer-open]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-ph-drawer-open]')) { e.preventDefault(); openDrawer(); }
    else if (e.target.closest('[data-ph-drawer-close]')) { e.preventDefault(); closeDrawer(); }
    else if (e.target.closest('[data-ph-drawer-search]')) {
      // Reveal the Ask box (search) inside the drawer and focus it
      e.preventDefault();
      var ask = document.querySelector('.ph-sidebar .ph-ask');
      if (ask) {
        ask.classList.add('is-revealed');
        var input = ask.querySelector('input');
        if (input) input.focus();
      }
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('ph-drawer-open')) closeDrawer();
  });
})();
